import React from 'react';
import { CAPTCHA_COPY } from '../../constants/abdmCopy';

/**
 * CaptchaField — the picture + answer box shown before an ABHA OTP first send (NHA VRFY_ABHA_301).
 * Driven by useAbhaCaptcha (pass its return value as `captcha`). Renders nothing while idle (the
 * identifier is not valid yet) or when the captcha is switched off. `error` is a captcha error from the
 * last send (CAPTCHA_REQUIRED / CAPTCHA_INVALID / CAPTCHA_UNAVAILABLE), shown here by the box.
 */
export default function CaptchaField({ captcha, error, isDark, disabled = false, id = 'abha-captcha' }) {
  if (captcha.status === 'idle' || captcha.status === 'disabled') return null;

  const text = isDark ? '#e2e8f0' : '#1f2937';
  const muted = isDark ? '#94a3b8' : '#6b7280';
  const btn = {
    fontSize: 12, fontWeight: 600, padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
    border: `1px solid ${isDark ? '#475569' : '#94a3b8'}`,
    background: isDark ? '#1e293b' : '#fff',
    color: isDark ? '#e2e8f0' : '#1f2937',
  };
  const loadError = captcha.status === 'error' ? (captcha.error?.userMessage || CAPTCHA_COPY.loadFailed) : '';
  const errorText = error?.userMessage || loadError;

  return (
    <div style={{ marginBottom: 16 }} data-testid="abha-captcha">
      <label htmlFor={id} style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: muted }}>
        {CAPTCHA_COPY.label}
      </label>

      {captcha.status === 'loading' && (
        <p role="status" style={{ fontSize: 12, color: muted, margin: '0 0 8px' }}>{CAPTCHA_COPY.loading}</p>
      )}

      {captcha.status === 'ready' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <img
            src={`data:image/png;base64,${captcha.image}`}
            alt={CAPTCHA_COPY.imageAlt}
            style={{ height: 56, borderRadius: 6, border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`, background: '#fff' }}
          />
          <button type="button" onClick={captcha.refresh} disabled={disabled} style={btn}>
            {CAPTCHA_COPY.refresh}
          </button>
        </div>
      )}

      {captcha.status === 'error' && (
        <button type="button" onClick={captcha.refresh} disabled={disabled} style={{ ...btn, marginBottom: 8 }}>
          {CAPTCHA_COPY.retry}
        </button>
      )}

      {captcha.status === 'ready' && (
        <input
          id={id}
          type="text"
          value={captcha.text}
          onChange={(e) => captcha.setText(e.target.value.replace(/\s/g, '').slice(0, 6))}
          disabled={disabled}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={6}
          aria-invalid={Boolean(error)}
          aria-describedby={errorText ? `${id}-error` : undefined}
          style={{
            width: '100%', padding: '10px 12px', fontSize: 15, letterSpacing: 3, boxSizing: 'border-box',
            borderRadius: 8, outline: 'none', fontFamily: 'inherit', textTransform: 'uppercase',
            border: `1.5px solid ${error ? '#ef4444' : isDark ? '#475569' : '#cbd5e1'}`,
            background: isDark ? '#0f172a' : '#fff', color: text,
          }}
        />
      )}

      {errorText && (
        <p id={`${id}-error`} role="alert" style={{ fontSize: 12, marginTop: 6, marginBottom: 0, color: isDark ? '#fca5a5' : '#b91c1c' }}>
          {errorText}
        </p>
      )}
    </div>
  );
}
